import React, { useEffect, useRef, useState } from "react";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
let recaptchaScriptPromise = null;

function loadRecaptchaScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.grecaptcha) return Promise.resolve();
  if (!recaptchaScriptPromise) {
    recaptchaScriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector("script[src*='google.com/recaptcha/api.js']");
      if (existing) {
        existing.addEventListener("load", resolve, { once: true });
        existing.addEventListener("error", reject, { once: true });
        return;
      }
      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }
  return recaptchaScriptPromise;
}

export function isRecaptchaEnabled() {
  return Boolean(SITE_KEY);
}

export default function RecaptchaBox({ onTokenChange }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!SITE_KEY || !containerRef.current) return;
    let cancelled = false;

    loadRecaptchaScript()
      .then(() => {
        if (cancelled || !window.grecaptcha || widgetIdRef.current !== null) return;
        window.grecaptcha.ready(() => {
          if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
          widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
            sitekey: SITE_KEY,
            callback: (token) => onTokenChange(token),
            "expired-callback": () => onTokenChange(""),
            "error-callback": () => {
              onTokenChange("");
              setLoadError(true);
            },
          });
        });
      })
      .catch(() => setLoadError(true));

    return () => {
      cancelled = true;
    };
  }, [onTokenChange]);

  if (!SITE_KEY) return null;

  return (
    <div className="space-y-2">
      <div ref={containerRef} className="min-h-[78px]" />
      {loadError && (
        <p className="text-xs text-red-600">
          CAPTCHA could not load. Please refresh the page and try again.
        </p>
      )}
    </div>
  );
}
